/**
 * Deux formes, une seule lecture :
 * - `YYYY-MM-DD` est un jour calendaire, construit en heure locale.
 * - une valeur avec une heure (`2026-01-14T23:00:00+00:00`) est un instant.
 *   Le jour et l'heure affichés sont ceux du fuseau de l'utilisateur :
 *   23:00 UTC le 14 janvier est le 15 janvier à minuit à Paris.
 */

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Jour calendaire local, au format `YYYY-MM-DD`. */
export function formatLocalDate(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function parseLocalDate(
	value: Date | string | null | undefined
): Date | undefined {
	if (value == null || value === '') return undefined;
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? undefined : value;
	}

	const calendar = CALENDAR_DATE.exec(value);
	if (calendar) {
		return new Date(
			Number(calendar[1]),
			Number(calendar[2]) - 1,
			Number(calendar[3])
		);
	}

	const instant = new Date(value);
	return Number.isNaN(instant.getTime()) ? undefined : instant;
}

/** Jour calendaire local d'une date ou d'un instant. */
export function asLocalDateString(
	value: Date | string | null | undefined
): string | undefined {
	const date = parseLocalDate(value);
	return date ? formatLocalDate(date) : undefined;
}

/** Date et heure locales. Une chaîne vide si la valeur est absente ou illisible. */
export function formatLocalDateTime(
	value: Date | string | null | undefined,
	options?: Intl.DateTimeFormatOptions
): string {
	const date = parseLocalDate(value);
	return date ? date.toLocaleString('fr-FR', options) : '';
}
